/**
 * Shared button style constants for styled-components.
 * Matches legacy Contently _buttons.sass exactly.
 *
 * Usage:
 *   import { btnPrimary, btnSecondary, btnDelete, btnBase } from '../../styles/buttonStyles';
 *   const SaveButton = styled.button`${btnPrimary}`;
 *   const CreateButton = styled(Link)`${btnSecondary}`;
 */
import { css } from 'styled-components';

/* -- Color tokens (from legacy _variables.scss) -- */
const colors = {
  bluePrimary: '#00a6c2',
  bluePrimaryDark: '#0094AB',
  bluePrimaryLight: '#17B7D1',
  blueSecondaryLight: '#cff5fd',
  blueSecondary: '#ebfcff',
  blueSecondaryDark: '#57cadc',
  graySecondaryLight: '#f8f8f8',
  graySecondaryDark: '#d4d4d4',
  gray: '#666',
  white: '#fff',
  red: '#E51D1D',
} as const;

/* -- Base button (shared across all variants) -- */
export const btnBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  text-decoration: none;
  white-space: nowrap;
  line-height: 100%;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease;
  height: 30px;
  padding: 0 9px;

  &:hover {
    text-decoration: none;
  }

  &:disabled,
  &[disabled] {
    background: ${colors.graySecondaryDark};
    border-color: ${colors.graySecondaryDark};
    color: ${colors.graySecondaryLight};
    cursor: not-allowed;
    transition: none;
  }
`;

/* -- Medium size (14px / 40px height) -- */
export const btnMedium = css`
  ${btnBase}
  font-size: 14px;
  height: 40px;
  padding: 0 12px;
`;

/* -- Primary: teal bg, white text -- */
export const btnPrimary = css`
  ${btnBase}
  background: ${colors.bluePrimary};
  border-color: ${colors.bluePrimaryDark};
  color: ${colors.white};

  &:hover:not(:disabled) {
    background: ${colors.bluePrimaryLight};
    color: ${colors.white};
  }
`;

export const btnPrimaryMedium = css`
  ${btnMedium}
  background: ${colors.bluePrimary};
  border-color: ${colors.bluePrimaryDark};
  color: ${colors.white};

  &:hover:not(:disabled) {
    background: ${colors.bluePrimaryLight};
    color: ${colors.white};
  }
`;

/* -- Secondary: light blue bg, teal text -- */
export const btnSecondary = css`
  ${btnBase}
  background: ${colors.blueSecondaryLight};
  border-color: ${colors.blueSecondaryDark};
  color: ${colors.bluePrimary};

  &:hover:not(:disabled) {
    background: ${colors.blueSecondary};
    color: ${colors.bluePrimary};
  }
`;

export const btnSecondaryMedium = css`
  ${btnMedium}
  background: ${colors.blueSecondaryLight};
  border-color: ${colors.blueSecondaryDark};
  color: ${colors.bluePrimary};

  &:hover:not(:disabled) {
    background: ${colors.blueSecondary};
    color: ${colors.bluePrimary};
  }
`;

/* -- Delete: gray bg, red text -- */
export const btnDelete = css`
  ${btnBase}
  background: ${colors.graySecondaryLight};
  border-color: ${colors.graySecondaryDark};
  color: ${colors.red};

  &:hover:not(:disabled) {
    background: ${colors.white};
  }
`;

/* -- Transparent: just teal text, underline on hover -- */
export const btnTransparent = css`
  ${btnBase}
  background: transparent;
  border-color: transparent;
  color: ${colors.bluePrimary};

  &:hover:not(:disabled) {
    text-decoration: underline;
    border-color: transparent;
  }
`;
